import { useEffect, useRef, useState } from "react";
import { XMarkIcon, CameraIcon } from "@heroicons/react/24/outline";
import ButtonSpinner from "../controls/ButtonSpinner";

export default function CameraCaptureModal(props) {
  const { open, setOpen, onCapture } = props;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const capturedImageUrlRef = useRef(null);

  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  // Initialize camera when modal opens
  useEffect(() => {
    if (open) {
      initializeCamera();
    } else {
      // Cleanup when modal closes
      cleanup();
    }

    return () => {
      cleanup();
    };
  }, [open]);

  // Handle video playback when stream is available
  useEffect(() => {
    if (stream && videoRef.current && !capturedImage) {
      const video = videoRef.current;
      
      // Set the stream
      if (video.srcObject !== stream) {
        video.srcObject = stream;
      }
      
      // Try to play the video
      const playVideo = async () => {
        try {
          await video.play();
          setVideoReady(true);
        } catch (err) {
          console.error("Error playing video:", err);
          // Try again after a short delay
          setTimeout(() => {
            video.play()
              .then(() => setVideoReady(true))
              .catch((err2) => {
                console.error("Error playing video (retry):", err2);
                setError("Failed to start video. Please ensure camera permissions are granted.");
              });
          }, 100);
        }
      };

      // Handle when metadata is loaded
      const handleLoadedMetadata = () => {
        playVideo();
      };

      // Handle when video starts playing
      const handlePlaying = () => {
        setVideoReady(true);
      };

      // Handle video errors
      const handleError = (e) => {
        console.error("Video error:", e);
        setError("Video playback error. Please try again.");
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('error', handleError);
      
      // If video is already ready, try playing immediately
      if (video.readyState >= 1) {
        playVideo();
      } else {
        // Wait for metadata to load
        video.load();
      }

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('error', handleError);
      };
    } else if (!stream) {
      setVideoReady(false);
    }
  }, [stream, capturedImage]);

  const initializeCamera = async () => {
    setIsInitializing(true);
    setError(null);
    setCapturedImage(null);
    setVideoReady(false);

    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Camera capture is not supported in this browser. Please use file upload instead."
        );
      }

      // Request camera access - will work with USB cameras when connected
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = mediaStream;
      setStream(mediaStream);
    } catch (err) {
      const errorMessages = {
        NotAllowedError: "Please allow camera permissions in your browser settings.",
        PermissionDeniedError: "Please allow camera permissions in your browser settings.",
        NotFoundError: "No camera found. Please connect a USB camera or use file upload.",
        DevicesNotFoundError: "No camera found. Please connect a USB camera or use file upload.",
        NotReadableError: "Camera is already in use by another application.",
        TrackStartError: "Camera is already in use by another application.",
        OverconstrainedError: "Camera does not support the required settings.",
        ConstraintNotSatisfiedError: "Camera does not support the required settings.",
      };

      const errorMessage = `Could not access camera. ${
        errorMessages[err.name] || err.message || "Please try again or use file upload."
      }`;

      setError(errorMessage);
    } finally {
      setIsInitializing(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !stream) {
      return;
    }

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob, then to File
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create File object from blob
            const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
              type: "image/jpeg",
            });

            // Create preview URL
            const previewUrl = URL.createObjectURL(blob);
            capturedImageUrlRef.current = previewUrl;
            setCapturedImage(previewUrl);
          }
          setIsCapturing(false);
        },
        "image/jpeg",
        0.95 // 95% quality
      );
    } catch (err) {
      setError("Failed to capture photo. Please try again.");
      setIsCapturing(false);
    }
  };

  const retakePhoto = () => {
    // Clean up the preview URL
    if (capturedImageUrlRef.current) {
      URL.revokeObjectURL(capturedImageUrlRef.current);
      capturedImageUrlRef.current = null;
    }
    setCapturedImage(null);
  };

  const usePhoto = () => {
    if (!capturedImage || !canvasRef.current) {
      return;
    }

    try {
      // Convert canvas to blob, then to File
      canvasRef.current.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            onCapture(file);
          }
          handleClose();
        },
        "image/jpeg",
        0.95
      );
    } catch (err) {
      setError("Failed to process photo. Please try again.");
    }
  };

  const cleanup = () => {
    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Clean up preview URL using ref to avoid stale closure
    if (capturedImageUrlRef.current) {
      URL.revokeObjectURL(capturedImageUrlRef.current);
      capturedImageUrlRef.current = null;
    }

    // Clear video srcObject
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setStream(null);
    setCapturedImage(null);
    setError(null);
  };

  const handleClose = () => {
    cleanup();
    setOpen({
      childComponent: null,
      open: false,
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold leading-6 text-gray-900">
          Capture Photo from Camera
        </h3>
        <button
          type="button"
          className="text-gray-400 hover:text-gray-500"
          onClick={handleClose}
        >
          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {isInitializing && (
        <div className="flex flex-col items-center justify-center p-8">
          <ButtonSpinner spin={true} text="Initializing camera..." />
        </div>
      )}

      {!isInitializing && !error && stream && (
        <div className="space-y-4">
          {/* Live video preview or captured image */}
          <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3', minHeight: '300px' }}>
            {capturedImage ? (
              <img
                src={capturedImage}
                alt="Captured"
                className="w-full h-full object-contain"
              />
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-contain ${!videoReady ? 'opacity-0' : ''}`}
                  style={{ transform: "scaleX(-1)", minHeight: '300px' }}
                />
                {!videoReady && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ButtonSpinner spin={true} text="Loading camera..." />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Hidden canvas for image processing */}
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* Action buttons */}
          <div className="flex justify-center gap-3">
            {capturedImage ? (
              <>
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-purple"
                  onClick={retakePhoto}
                >
                  Retake
                </button>
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-purple"
                  onClick={usePhoto}
                  disabled={isCapturing}
                >
                  <ButtonSpinner spin={isCapturing} text="Use this photo" />
                </button>
              </>
            ) : (
              <button
                type="button"
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main-purple disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={capturePhoto}
                disabled={isCapturing}
              >
                <CameraIcon className="h-6 w-6 mr-2" aria-hidden="true" />
                <ButtonSpinner spin={isCapturing} text="Capture" />
              </button>
            )}
          </div>
        </div>
      )}

      {!isInitializing && error && (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

